import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import CreateRestaurant from '../components/Restaurant/CreateRestaurant'
import {
  Box, Button, Dialog, DialogContent, DialogTitle,
  Container, Typography, CircularProgress, Chip,
  IconButton, InputBase, Paper, Snackbar
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import StorefrontIcon from '@mui/icons-material/Storefront'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { withTranslation } from 'react-i18next'

const RESTAURANT_BY_OWNER = gql`
  query RestaurantByOwner($id: String) {
    restaurantByOwner(id: $id) {
      _id
      email
      userType
      restaurants {
        _id
        name
        slug
        image
        logo
        address
        shopType
        deliveryTime
        minimumOrder
        isActive
      }
    }
  }
`

const DELETE_RESTAURANT = gql`
  mutation DeleteRestaurant($id: ID!) {
    deleteRestaurant(id: $id) {
      _id
      isActive
    }
  }
`

const Restaurant = props => {
  const { t } = props
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [owner, setOwner] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [snackError, setSnackError] = useState(null)

  const vendorFilterId = localStorage.getItem('vendorFilterId')
  const vendorId = vendorFilterId || localStorage.getItem('vendorId')

  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    RESTAURANT_BY_OWNER,
    { variables: { id: vendorId }, fetchPolicy: 'network-only' }
  )

  const [mutate, { loading: mutateLoading }] = useMutation(DELETE_RESTAURANT, {
    refetchQueries: [{ query: RESTAURANT_BY_OWNER, variables: { id: vendorId } }],
    onError: err => setSnackError(err.graphQLErrors?.[0]?.message || 'Something went wrong')
  })

  const allRestaurants = data?.restaurantByOwner?.restaurants ?? []
  const activeCount = allRestaurants.filter(r => r.isActive).length

  const filtered = searchQuery.length < 2
    ? allRestaurants
    : allRestaurants.filter(r =>
        (r.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.address || '').toLowerCase().includes(searchQuery.toLowerCase())
      )

  return (
    <>
      {/* Hero banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
        px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden'
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StorefrontIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>My Restaurants</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage your restaurant listings</Typography>
            </Box>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={() => { setOwner(data?.restaurantByOwner?._id ?? vendorId); setIsModalVisible(true) }}
            sx={{ bgcolor: '#f97316', color: '#fff', fontWeight: 700, borderRadius: '12px', px: 2.5, py: 1.2, fontSize: '0.875rem', textTransform: 'none', '&:hover': { bgcolor: '#ea6c0a' }, boxShadow: '0 4px 12px rgba(249,115,22,0.4)' }}
          >
            {t('AddNewRestaurant')}
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Header row with stats + search */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', px: 2, py: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1d4ed8' }}>{loadingQuery ? '…' : allRestaurants.length}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600 }}>Total</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', px: 2, py: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#16a34a' }}>{loadingQuery ? '…' : activeCount}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>Active</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', px: 2, py: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#d97706' }}>{loadingQuery ? '…' : allRestaurants.length - activeCount}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>Inactive</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', px: 2, py: 1, width: 280, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
            <InputBase placeholder="Search restaurants..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }} />
          </Box>
        </Box>

        {errorQuery && (
          <Box sx={{ px: 3, py: 2, mb: 2, bgcolor: '#fef2f2', borderRadius: '12px' }}>
            <Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {errorQuery.message}</Typography>
          </Box>
        )}

        {loadingQuery ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <StorefrontIcon sx={{ fontSize: 52, color: '#e2e8f0', mb: 1 }} />
            <Typography sx={{ color: '#94a3b8' }}>No restaurants found</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2.5 }}>
            {filtered.map(r => (
              <Paper
                key={r._id}
                elevation={0}
                sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'all 0.18s', '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,0.11)', transform: 'translateY(-2px)', borderColor: '#cbd5e1' } }}
              >
                {/* Image area — clickable to enter dashboard */}
                <Box
                  onClick={() => {
                    localStorage.setItem('restaurantId', r._id)
                    localStorage.setItem('restaurantImage', r.image)
                    localStorage.setItem('restaurantName', r.name)
                    props.history.push('/admin/dashboard/' + r.slug)
                  }}
                  sx={{ position: 'relative', height: 150, bgcolor: '#f1f5f9', overflow: 'hidden', cursor: 'pointer' }}
                >
                  {r.image
                    ? <img src={r.image} alt={r.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><StorefrontIcon sx={{ fontSize: 52, color: '#cbd5e1' }} /></Box>
                  }
                  {/* Logo */}
                  {r.logo && (
                    <Box sx={{ position: 'absolute', bottom: 10, left: 12, width: 46, height: 46, borderRadius: '12px', border: '2.5px solid #fff', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', bgcolor: '#fff' }}>
                      <img src={r.logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  )}
                  {/* Status badge */}
                  <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: '13px !important', color: `${r.isActive ? '#16a34a' : '#d97706'} !important` }} />}
                      label={r.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{ bgcolor: r.isActive ? 'rgba(220,252,231,0.95)' : 'rgba(254,243,199,0.95)', color: r.isActive ? '#16a34a' : '#d97706', fontWeight: 700, fontSize: '0.72rem', height: 22, backdropFilter: 'blur(4px)', '& .MuiChip-icon': { ml: '5px' } }}
                    />
                  </Box>
                </Box>

                {/* Card body */}
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.6 }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.3, cursor: 'pointer', '&:hover': { color: '#3b82f6' } }}
                      onClick={() => {
                        localStorage.setItem('restaurantId', r._id)
                        localStorage.setItem('restaurantImage', r.image)
                        localStorage.setItem('restaurantName', r.name)
                        props.history.push('/admin/dashboard/' + r.slug)
                      }}
                    >
                      {r.name}
                    </Typography>
                    {r.shopType && (
                      <Chip label={r.shopType} size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.65rem', height: 20, fontWeight: 600, ml: 0.5, flexShrink: 0 }} />
                    )}
                  </Box>

                  {r.address && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 13, color: '#94a3b8', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>{r.address}</Typography>
                    </Box>
                  )}

                  {/* Stats row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {r.deliveryTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{r.deliveryTime} min</Typography>
                      </Box>
                    )}
                    {r.minimumOrder != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <ShoppingBagIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>${r.minimumOrder} min</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Enable / Disable button */}
                  <Button
                    fullWidth size="small" variant="outlined"
                    onClick={e => { e.stopPropagation(); mutate({ variables: { id: r._id } }) }}
                    disabled={mutateLoading}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: r.isActive ? '#fca5a5' : '#86efac', color: r.isActive ? '#ef4444' : '#22c55e', '&:hover': { bgcolor: r.isActive ? '#fef2f2' : '#f0fdf4', borderColor: r.isActive ? '#ef4444' : '#22c55e' } }}
                  >
                    {r.isActive ? t('Disable') : t('Enable')}
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Container>

      {/* Add Restaurant Dialog */}
      <Dialog open={isModalVisible} onClose={() => setIsModalVisible(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">{t('AddNewRestaurant')}</Typography>
          <IconButton size="small" onClick={() => setIsModalVisible(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <CreateRestaurant owner={owner} onClose={() => { setIsModalVisible(false); refetch() }} />
        </DialogContent>
      </Dialog>

      <Snackbar open={!!snackError} autoHideDuration={5000} onClose={() => setSnackError(null)} message={snackError} />
    </>
  )
}

export default withTranslation()(Restaurant)
